import type { UnknownAction } from '@reduxjs/toolkit'
import { ApiResponse } from '@axilon-apps/libs-shared-api'

import AuthenticationStorage from '../../shared/core/services/authentication-token'
import { authenticationLogout } from '../actions/authentication-actions'
import { setToken } from '../slices/authentication-slice'
import { apiServiceInitiator } from '../instances'

export type RefreshAuthenticationResult =
  | { ok: true; refreshed: boolean }
  | { ok: false; error: unknown }

let inFlightRefresh: Promise<RefreshAuthenticationResult> | null = null

/**
 * The single token-refresh implementation shared by every API surface
 * (identity's own `baseQuery` and the ontology base query / connect
 * clients). Consumers must never re-implement refresh — they call this.
 *
 * Behaviour:
 * - Without `force`, a no-op while the access token is still fresh
 *   (`AuthenticationTokenService.isExpired()` already applies a 5-minute
 *   early buffer). Pass `force: true` from reactive 401 handlers where
 *   the server has rejected a locally-fresh-looking token.
 * - Concurrent calls coalesce into one `POST /api/v2/sessions/refresh`
 *   (single in-flight promise), so a burst of parallel 401s refreshes once.
 * - On success dispatches `setToken`; the caller's store must have
 *   `authenticationSetTokenListener` registered, which persists the new
 *   token into `AuthenticationStorage` synchronously during the dispatch —
 *   callers can re-read the storage immediately after this resolves.
 * - On failure dispatches `authenticationLogout({ skipSessionDelete: true })`
 *   and resolves `{ ok: false }`; it never throws.
 */
export async function refreshAuthenticationToken(
  dispatch: (action: UnknownAction) => unknown,
  options: { force?: boolean } = {}
): Promise<RefreshAuthenticationResult> {
  if (!options.force && !AuthenticationStorage.accessToken?.isExpired()) {
    return { ok: true, refreshed: false }
  }

  if (inFlightRefresh) {
    return inFlightRefresh
  }

  inFlightRefresh = (async (): Promise<RefreshAuthenticationResult> => {
    try {
      const auth = await apiServiceInitiator().authenticate()

      dispatch(setToken(auth.data()))

      return { ok: true, refreshed: true }
    } catch (error) {
      dispatch(authenticationLogout({ skipSessionDelete: true }))

      return {
        ok: false,
        error: error instanceof ApiResponse ? error.error() : error
      }
    } finally {
      inFlightRefresh = null
    }
  })()

  return inFlightRefresh
}
